import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MoreHoriz } from '@mui/icons-material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import PendingIcon from '@mui/icons-material/Pending'
import { IconButton } from '@mui/material'
import {
  GridColDef,
  GridRowsProp,
  GridToolbarContainer
} from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { PAYMENT_METHODS } from 'constants/payment'
import { PaymentModal } from 'pages/Payment/components'

import PaymentAPI from '../../api/orbitV1/PaymentAPI'
import QuotationAPI from '../../api/orbitV1/QuotationAPI'
import CustomSearchbar from '../../common/AbstractDataGrid/CustomSearchbar'
import MUIDataGrid from '../../common/AbstractDataGrid/DataGridMUI'
import PageLayout from '../../common/Layout/Page/PageLayout'
import { ColorBoxContent } from '../../common/ui/colorBox'
import JoyrideContext from '../../context/joyride/joyrideContext'
import { getDate } from '../../helpers/date'
import OrbitSession from '../../hooks/useCookies'

import QuotationDialog from './components/QuotationDialog'

interface IQuotation {
  _id: string
  createdAt: Date
  expiryAt: Date
  price: number
  status: string
}

interface IQuotationDetails {
  _id: string
  items: {
    name: string
    desc: string
    price: number
    quantity: number
  }[]
  user: {
    name: string
    contactNumber: number
    email: string
  }[]
  price: number
  status: string
  createdAt: Date
  expiryAt: Date
}

interface IOrgDetails {
  name: string
  uen: string
  email: string
  billingAddress: string
  deliveryAddress: string
}

function QuotationToolbar() {
  return (
    <GridToolbarContainer>
      <CustomSearchbar />
    </GridToolbarContainer>
  )
}

export default function ViewQuotation() {
  const email = OrbitSession.get('email')
  const userId = OrbitSession.get('_id')
  const [checked, setChecked] = useState(false)
  const [isQuotationDialogOpen, setQuotationDialogOpen] = useState(false)
  const ORDER_LIMIT = 150
  const [quotations, setQuotations] = useState<IQuotation[] | []>([])
  const [orgDetails, setOrgDetails] = useState<
    IOrgDetails | Record<string, never>
  >({})
  const [quotationDetails, setQuotationDetails] = useState<
    IQuotationDetails | Record<string, never>
  >({})
  const [unexpectedError, setUnexpectedError] = useState('')
  const orgId = OrbitSession.get('orgId')
  const joyrideContext = useContext(JoyrideContext)
  const { tourActive, setRun, setStepIndex } = joyrideContext

  /* Payment variables */
  const [isPaymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(
    PAYMENT_METHODS.CREDIT_CARD
  )
  const [quotationPrice] = useState(0)
  const [quotationMetaInfo] = useState({})

  const {
    status,
    data: orgQuotations,
    error: getQuotationsError,
    refetch: refetchQuotations
  } = useQuery(['getOrgQuotations', 'viewQuotations'], async () => {
    const { data } = await QuotationAPI.getOrgQuotations(orgId, ORDER_LIMIT)
    return data
  })

  useEffect(() => {
    if (status === 'success') {
      setQuotations(orgQuotations.quotations)
      setOrgDetails(orgQuotations.organization)
      if (tourActive) {
        setTimeout(() => {
          setRun(true)
          setStepIndex(8)
        }, 500)
      }
    }
  }, [orgQuotations, status])

  useEffect(() => {
    if (getQuotationsError && !quotations) {
      toast.error('Error loading quotations!')
    }
  }, [getQuotationsError])

  // TODO: migrate to stripe elements
  const handlePayment = async () => {
    // TODO: remove once migrated to use payment modal
    setUnexpectedError('')
    const orderDetails = {
      newUser: false, // To disable creation of organisation and user
      quoteId: quotationDetails?._id,
      orgId,
      type: 'quotation',
      email,
      price: quotationDetails?.price,
      paymentMethod: 'Credit Card', // One option for quotation for now
      userId,
      itemName: `Quotation No. ${quotationDetails?._id}`
    }

    try {
      // TODO: remove once migrated to use payment modal
      const paymentResponse = await PaymentAPI.makePayment(orderDetails)

      // TODO: remove once migrated to use payment modal
      if (paymentResponse.data) {
        OrbitSession.set({ paymentWindow: 'quotation' })
        window.open(
          paymentResponse.data.sessionUrl,
          '_blank',
          'top=300,left=300,width=800,height=500'
        )
      }
      // TODO: uncomment once quotation template is fixed
      // setQuotationPrice(quotationDetails?.price)
      // setQuotationMetaInfo({
      //   data: {
      //     orgId,
      //     email,
      //     userId,
      //     quoteId: quotationDetails._id,
      //     itemName: `Quotation No. ${quotationDetails?._id}`
      //   }
      // })
      // setPaymentDialogOpen(true)
    } catch (error) {
      setUnexpectedError('There is some issue with processing payment')
      toast.error(unexpectedError)
    }
  }

  const onCheckboxClick = (event) => {
    setChecked(event.target.checked)
  }

  const checkStatus = quotationDetails?.status === 'PAID'

  // DATA GRID COMPONENT
  const rows: GridRowsProp = quotations.map((quotation, index) => {
    return {
      id: quotation._id,
      quotationNo: quotation._id,
      index,
      creationDate: getDate(quotation.createdAt),
      validity: getDate(quotation.expiryAt),
      noOfSims: quotation.items
        .map((item) => item.quantity)
        .reduce((prev, next) => prev + next, 0),
      items: quotation.items,
      totalAmount: '$ ' + quotation.price,
      status: quotation.status,
      quotation
    }
  })

  const statusChips = {
    PAID: <CheckCircleIcon sx={{ color: 'green' }} />,
    PENDING: <PendingIcon sx={{ color: 'orange' }} />,
    ERROR: <ErrorIcon sx={{ color: 'red' }} />
  }

  const columns: GridColDef[] = [
    { field: 'quotationNo', headerName: 'Quotation no.', width: 250 },
    { field: 'creationDate', headerName: 'Creation Date', width: 150 },
    { field: 'validity', headerName: 'Validity', width: 150 },
    {
      field: 'noOfSims',
      headerName: 'No. of SIMs',
      width: 50,
      valueGetter: (params) => {
        const quotation = params.row
        return quotation.items
          .map((item) => item.quantity)
          .reduce((prev, next) => prev + next, 0)
      }
    },
    { field: 'totalAmount', headerName: 'Total Amount', width: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const quotation = params.row
        return statusChips[quotation.status]
      }
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      renderCell: (params) => {
        return (
          <IconButton
            key={params.row.index}
            id={`viewQuotation__viewBtn_${params.row.index}`}
            onClick={() => {
              const quotation = params.row
              setQuotationDetails(quotation.quotation)
              setQuotationDialogOpen(true)
            }}
            data-cy='actionBtn'
          >
            <MoreHoriz />
          </IconButton>
        )
      }
    }
  ]

  return (
    <PageLayout header='View Quotation'>
      <ColorBoxContent>
        {/* DATA GRID COMPONENT` */}
        <div id='quotes__quotationDataGrid'>
          <MUIDataGrid
            initialState={{
              sorting: {
                sortModel: [{ field: 'creationDate', sort: 'desc' }]
              }
            }}
            rows={rows}
            columns={columns}
            components={{ Toolbar: QuotationToolbar }}
          />
        </div>
      </ColorBoxContent>
      {isQuotationDialogOpen && (
        <QuotationDialog
          isDialogOpen={isQuotationDialogOpen}
          setDialogOpen={setQuotationDialogOpen}
          checked={checked}
          checkStatus={checkStatus}
          onCheckboxClick={onCheckboxClick}
          handlePayment={handlePayment}
          quotationDetails={quotationDetails}
          orgDetails={orgDetails}
        />
      )}
      {isPaymentDialogOpen && (
        <PaymentModal
          setDialogOpen={setPaymentDialogOpen}
          isDialogOpen={isPaymentDialogOpen}
          amountPayable={quotationPrice} // pass amount to pay into modal
          orgId={orgId}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          PAYMENT_METHODS={PAYMENT_METHODS}
          metaInfo={quotationMetaInfo}
          email={email}
          handleNext={refetchQuotations}
          variant='quotation'
        />
      )}
    </PageLayout>
  )
}
